import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "@/components/app/Logo";
import { CheckinForm, RATINGS } from "@/components/app/DecisionDialogs";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

export default function DecisionReview() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/public/decision-review/${token}`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (c) => {
    setBusy(true);
    try {
      setD((await api.post(`/public/decision-review/${token}`, c)).data);
      setSaved(true);
    } catch (e) {
      setError(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-100 flex flex-col items-center px-4 py-10">
      <Link to="/" className="mb-8"><Logo invert /></Link>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0E1424] p-6" data-testid="decision-review-page">
        {!d && !error && <Loader2 className="h-5 w-5 animate-spin mx-auto" />}
        {error && !d && <p className="text-sm text-red-300" data-testid="decision-review-error">{error}</p>}
        {d && (
          <>
            <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Decision review</div>
            <h1 className="mt-2 text-xl font-semibold" data-testid="decision-review-title">{d.title}</h1>
            <p className="mt-1 text-sm text-slate-400">Decided {fmtDate(d.decided_at)}{d.decided_by && ` by ${d.decided_by}`}</p>
            {d.expected_outcome && <p className="mt-3 text-sm"><span className="text-slate-400">Expected:</span> {d.expected_outcome}</p>}
            {d.question && <p className="mt-4 text-sm border-l-2 border-emerald-500/60 pl-3 text-slate-200" data-testid="decision-review-question">{d.question}</p>}
            {saved ? (
              <div className="mt-5 flex gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] p-3.5 text-sm text-emerald-100" data-testid="decision-review-saved">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />Thanks — your results and lessons are now part of your decision history.</div>
            ) : <div className="mt-5"><CheckinForm onSubmit={submit} busy={busy} dark /></div>}
            {error && <p className="mt-2 text-xs text-red-300">{error}</p>}
            {d.checkins?.length > 0 && (
              <ul className="mt-6 border-t border-white/10 pt-4 space-y-1.5 text-xs text-slate-400" data-testid="decision-review-history">
                {d.checkins.slice().reverse().map((c) => <li key={c.at}>{fmtDate(c.at)} — <b className="text-slate-200">{RATINGS[c.rating][0]}</b>{c.actual_result && `: ${c.actual_result}`}</li>)}
              </ul>)}
          </>
        )}
      </div>
      <p className="mt-6 text-xs text-slate-500">AccruallQ · Corporate Health Operating System</p>
    </div>
  );
}
